import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import { unwrapOralExamRunState } from '@/common/hooks/oralExam/useOralExamRunStatePoll';

type UseCompleteIfAbsentOnPeerGoneCloseOptions = {
  showClosingCountdown: boolean;
  secondsRemaining: number;
  completeIfAbsent: () => Promise<unknown>;
  defaultErrorMessage: string;
};

/**
 * When the 30s last-Q peer-gone countdown hits 0, POST complete-if-absent once.
 * On failure: inline error + manual retry; stay on the banner.
 */
export function useCompleteIfAbsentOnPeerGoneClose({
  showClosingCountdown,
  secondsRemaining,
  completeIfAbsent,
  defaultErrorMessage,
}: UseCompleteIfAbsentOnPeerGoneCloseOptions) {
  const [error, setError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const inFlightRef = useRef(false);
  const automaticCloseSentRef = useRef(false);

  const runComplete = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsCompleting(true);
    try {
      const data = await completeIfAbsent();
      const runState = unwrapOralExamRunState(data);
      if (runState?.state !== 'completed') {
        setError(defaultErrorMessage);
        return;
      }
      setError(null);
    } catch (err) {
      setError(getOralExamApiErrorMessage(err, defaultErrorMessage));
      throw err;
    } finally {
      inFlightRef.current = false;
      setIsCompleting(false);
    }
  }, [completeIfAbsent, defaultErrorMessage]);

  useEffect(() => {
    if (!showClosingCountdown) {
      automaticCloseSentRef.current = false;
      setError(null);
      return;
    }
    if (secondsRemaining > 0 || automaticCloseSentRef.current || inFlightRef.current) {
      return;
    }
    automaticCloseSentRef.current = true;
    runComplete().catch(() => undefined);
  }, [showClosingCountdown, secondsRemaining, runComplete]);

  const retry = useCallback(async () => {
    await runComplete();
  }, [runComplete]);

  return { error, isCompleting, retry };
}
